import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Download, FileEdit, CheckCircle2, LayoutTemplate } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { reports, Report } from '../lib/data';
function StatusBadge({
  status
}: {
  status: Report['status'];
}) {
  if (status === 'draft') {
    return <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200" data-id="element-1622">
        <FileEdit className="h-3 w-3" data-id="element-1623" />
        <span className="font-medium" data-id="element-1624">Bozza</span>
      </div>;
  }
  if (status === 'completed') {
    return <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200" data-id="element-1625">
        <CheckCircle2 className="h-3 w-3" data-id="element-1626" />
        <span className="font-medium" data-id="element-1627">Completato</span>
      </div>;
  }
  return null;
}
const templateLabels: Record<string, string> = {
  executive: 'Executive Summary',
  standard: 'Report Standard',
  full: 'Analisi Completa'
};
export function ReportGenerator() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'draft' | 'completed'>('all');
  const filtered = reports.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full" data-id="element-1628">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between mb-10 md:mb-12 gap-4" data-id="element-1629">
        <div data-id="element-1630">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1631">
            Report
          </h1>
        </div>
        <Button onClick={() => navigate('/reports/new')} className="rounded-full h-11 px-6 text-sm" data-id="element-1632">
          <Plus className="h-4 w-4 mr-1.5" data-id="element-1633" />
          Nuovo report
        </Button>
      </header>

      {/* Filter tabs */}
      <div className="bg-card border border-border rounded-lg" data-id="element-1634">
        <div className="flex items-center gap-6 px-6 pt-5 pb-0 text-sm" data-id="element-1635">
          {(['all', 'draft', 'completed'] as const).map(f => {
          const count = f === 'all' ? reports.length : reports.filter(r => r.status === f).length;
          const label = f === 'all' ? 'Tutti' : f === 'draft' ? 'Bozze' : 'Completati';
          const active = filter === f;
          return <button key={f} onClick={() => setFilter(f)} className={`pb-3 border-b-2 transition-colors ${active ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1636">
                {label}{' '}
                <span className="text-muted-foreground ml-1" data-id="element-1637">({count})</span>
              </button>;
        })}
        </div>

        <Separator data-id="element-1638" />

        {/* Reports list */}
        {filtered.length === 0 ? <div className="text-center py-16" data-id="element-1639">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-3" data-id="element-1640" />
            <p className="text-sm text-muted-foreground" data-id="element-1641">
              Nessun report in questo filtro
            </p>
          </div> : <div className="divide-y divide-border" data-id="element-1642">
            {filtered.map(r => <div key={r.id} className="w-full text-left py-5 md:py-6 px-6 hover:bg-muted/30 transition-colors flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-1643">
                {/* Left: info */}
                <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-1644">
                  <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-1645">
                    <LayoutTemplate className="h-4 w-4 text-muted-foreground" data-id="element-1646" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1647">
                    <div className="flex items-center gap-3 mb-1" data-id="element-1648">
                      <h3 className="font-[400] truncate text-[14px]" data-id="element-1649">
                        {r.name}
                      </h3>
                      <StatusBadge status={r.status} data-id="element-1650" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground font-normal" data-id="element-1651">
                      <span className="text-[12px]" data-id="element-1652">
                        {templateLabels[r.template]}
                      </span>
                      <span data-id="element-1653">·</span>
                      <span className="text-[12px]" data-id="element-1654">{r.dateRange}</span>
                      <span data-id="element-1655">·</span>
                      <span className="text-[12px]" data-id="element-1656">
                        Creato il{' '}
                        {new Date(r.createdAt).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: metrics & actions */}
                <div className="flex items-center justify-between md:justify-end gap-8 md:gap-12 flex-shrink-0 pl-14 md:pl-0 w-full md:w-auto" data-id="element-1657">
                  <div className="text-left md:text-right" data-id="element-1658">
                    <div className="text-xl md:text-2xl font-[400] leading-none" style={{
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1659">
                      {r.articleCount}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1" data-id="element-1660">
                      Articoli
                    </div>
                  </div>

                  <div className="flex items-center gap-2" data-id="element-1661">
                    {r.status === 'completed' ? <Button variant="outline" size="sm" className="rounded-full h-9" data-id="element-1662">
                        <Download className="h-3.5 w-3.5 mr-1.5" data-id="element-1663" />
                        Scarica PDF
                      </Button> : <Button variant="outline" size="sm" className="rounded-full h-9" onClick={() => navigate('/reports/new')} data-id="element-1664">
                        <FileEdit className="h-3.5 w-3.5 mr-1.5" data-id="element-1665" />
                        Modifica
                      </Button>}
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}